// What the header shows about the phone line. One clear state: "Ready", or
// "Phone not ready" with the reason (calls can't ring in this tab until it's
// ready again). Kept free of browser APIs so it can be unit-tested.

export type LineState =
  | 'starting' // loading settings
  | 'connecting'
  | 'updating' // the SIP login changed (new password): signing in again
  | 'ready' // registered, can call
  | 'offline' // connection lost, reconnecting
  | 'failed' // registration refused
  | 'no_phone' // this user has no phone line
  | 'other_tab' // another tab of this browser holds the line
  | 'error'; // settings could not be loaded (retrying)

export type LineTone = 'ok' | 'warn' | 'bad' | 'idle';

export interface LineStatus {
  tone: LineTone;
  /** short text for the header: "Ready", "Connecting…", "Phone not ready" */
  label: string;
  /** why it isn't ready (null when ready / connecting) */
  reason: string | null;
  /** the line can't take calls in this tab */
  notReady: boolean;
}

/**
 * @param online the browser's own idea of the network (navigator.onLine)
 */
export function lineStatus(line: LineState, online: boolean): LineStatus {
  if (line === 'no_phone') return { tone: 'idle', label: 'No phone line', reason: null, notReady: true };
  if (line === 'ready') {
    // registered, but the network just dropped: say so instead of a stale "Ready"
    if (!online) return notReady('warn', 'You’re offline');
    return { tone: 'ok', label: 'Ready', reason: null, notReady: false };
  }
  if (!online && line !== 'other_tab') return notReady('warn', 'You’re offline');
  switch (line) {
    case 'starting':
    case 'connecting':
      return { tone: 'warn', label: 'Connecting…', reason: null, notReady: true };
    case 'updating':
      return notReady('warn', 'Updating your phone login…');
    case 'offline':
      return notReady('warn', 'Reconnecting…');
    case 'other_tab':
      return notReady('idle', 'Open in another tab');
    case 'failed':
      return notReady('bad', 'Line unavailable');
    case 'error':
      return notReady('bad', 'Can’t load your settings');
  }
}

function notReady(tone: LineTone, reason: string): LineStatus {
  return { tone, label: 'Phone not ready', reason, notReady: true };
}

/** The dialer's hint when calling isn't possible. */
export function dialerHint(st: LineStatus): string {
  if (!st.notReady) return '';
  return st.reason ? `Phone not ready: ${st.reason.replace(/…$/, '')}.` : st.label;
}
